import { api } from "../api";
import { showAssignmentDetail } from "../components/assignment-detail";
import type { StudentAssignment, StudentPlanungData } from "../types";
import { generateIcs } from "../utils/calendar-export";
import { Icons } from "../utils/icons";
import { hasPortalFeature } from "../utils/portal-features";
import { esc } from "../utils/html";

function showToast(message: string): void {
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = message;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add("toast--visible"));
  setTimeout(() => {
    toast.classList.remove("toast--visible");
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

function renderAssignmentRow(a: StudentAssignment): string {
  const totalHours = a.durationWeeks * a.hoursPerWeek;
  const statusBadge = a.isPreliminary
    ? `<span style="background:#fef3c7;color:#92400e;padding:.1rem .5rem;border-radius:9999px;font-size:.75rem;font-weight:600">Vorläufig</span>`
    : a.isCurrent
    ? `<span style="background:#d1fae5;color:#059669;padding:.1rem .5rem;border-radius:9999px;font-size:.75rem;font-weight:600">Aktuell</span>`
    : a.isUpcoming
    ? `<span style="background:#dbeafe;color:#2563eb;padding:.1rem .5rem;border-radius:9999px;font-size:.75rem;font-weight:600">Demnächst</span>`
    : `<span style="background:#f3f4f6;color:#6b7280;padding:.1rem .5rem;border-radius:9999px;font-size:.75rem;font-weight:600">Abgeschlossen</span>`;

  const hasComment = !!(a.studentNote && a.studentNote.trim());
  const clickable = !!a.practiceId || hasComment;
  const commentIcon = hasComment
    ? `<span class="assignment-comment-icon" title="Hinweis von der Schule" style="color:#2563eb;flex-shrink:0">${Icons.chatBubbleLeftRight}</span>`
    : "";
  return `<tr data-assignment-id="${a.id}"${clickable ? ` style="cursor:pointer"` : ""}>
    <td>
      <div style="display:flex;align-items:center;gap:.5rem">
        <div style="width:10px;height:10px;border-radius:50%;background:${a.departmentColor};flex-shrink:0"></div>
        <span style="font-weight:500">${esc(a.departmentName)}</span>
      </div>
    </td>
    <td>
      <div style="display:flex;align-items:center;gap:.4rem">
        <span style="font-weight:500">${esc(a.practiceName || (a.isFreeInput ? "Freier Eintrag" : "–"))}</span>
        ${commentIcon}
      </div>
      ${a.practiceAddress ? `<div style="font-size:.8rem;color:#6b7280">${esc(a.practiceAddress)}</div>` : ""}
    </td>
    <td class="text-muted" style="white-space:nowrap">${formatDate(a.startDate)} – ${formatDate(a.endDate)}</td>
    <td class="text-muted" style="white-space:nowrap">${a.durationWeeks} Wo. / ${totalHours} Std.</td>
    <td>${statusBadge}</td>
  </tr>`;
}

function renderPeriodSeparator(name: string, startDate: string, endDate: string): string {
  return `<tr>
    <td colspan="5" style="background:#f3f4f6;padding:.5rem 1rem;font-size:.8rem;color:#6b7280;border-top:2px solid #e5e7eb">
      <strong style="color:#374151">${esc(name)}</strong>
      &nbsp;·&nbsp; ${formatDate(startDate)} – ${formatDate(endDate)}
    </td>
  </tr>`;
}

function renderProgressBar(completed: number, required: number, color: string): string {
  const pct = required > 0 ? Math.min((completed / required) * 100, 100) : 0;
  return `<div style="background:#e5e7eb;border-radius:9999px;height:8px;overflow:hidden;flex:1">
    <div style="background:${color};width:${pct}%;height:100%;border-radius:9999px"></div>
  </div>`;
}

type WunschDept = { id: string; nameDe: string; color: string; orphaned: boolean };

/** Rendert und verwaltet die Wunschliste interaktiv */
async function renderWunschliste(container: HTMLElement) {
  // Von der Schule abgeschaltet → Abschnitt gar nicht erst laden
  if (!hasPortalFeature("wunschliste")) return;

  // Fachbereiche + Wünsche laden – schlägt fehl wenn Schüler-Portal nicht aktiv
  let visibleDepts: { id: string; nameDe: string; color: string }[];
  let prefs: Awaited<ReturnType<typeof api.schuelerApp.wuensche>>;
  try {
    visibleDepts = await api.schuelerApp.fachbereiche();
    prefs = await api.schuelerApp.wuensche();
  } catch {
    // Schüler-Portal nicht aktiv oder nicht verfügbar → Abschnitt nicht anzeigen
    return;
  }
  if (visibleDepts.length === 0 && Object.keys(prefs).length === 0) return;

  // Fachbereiche für die Anzeige: sichtbare/aktive Fachbereiche + "verwaiste" Fachbereiche, die
  // inzwischen deaktiviert oder aus der Fachbereichsgruppe der Klasse entfernt wurden, für die aber
  // noch gespeicherte Wünsche existieren – sonst lassen sich diese Wünsche nie mehr entfernen.
  const depts: WunschDept[] = visibleDepts.map(d => ({ ...d, orphaned: false }));
  const visibleIds = new Set(visibleDepts.map(d => d.id));
  for (const [id, group] of Object.entries(prefs)) {
    if (!visibleIds.has(id)) depts.push({ id, nameDe: group.nameDe, color: group.color, orphaned: true });
  }

  // Verfügbare Praktikumsstellen pro (nicht verwaistem) Fachbereich laden – ein einzelner Fehler
  // darf nicht die gesamte Wunschliste ausblenden, daher pro Fachbereich einzeln abgesichert.
  const verfuegbareLists = await Promise.all(
    depts.map(d => (d.orphaned ? Promise.resolve([]) : api.schuelerApp.wuenschePraktika(d.id).catch(() => [])))
  );

  // State: aktuelle Wunschlisten pro Fachbereich (als geordnetes Array)
  const state: Record<string, { practiceId: number; practiceName: string }[]> = {};
  for (const dept of depts) {
    state[dept.id] = (prefs[dept.id]?.items || []).map(p => ({ practiceId: p.practiceId, practiceName: p.practiceName }));
  }

  const verfuegbar: Record<string, { id: number; name: string; ort?: string }[]> = {};
  for (let i = 0; i < depts.length; i++) {
    verfuegbar[depts[i].id] = verfuegbareLists[i];
  }

  // Verhindert überlappende Save-Requests für denselben Fachbereich (Lost-Update bei schnellen Mehrfachklicks)
  const savingDepts = new Set<string>();

  async function saveWunschliste(deptId: string) {
    savingDepts.add(deptId);
    rerenderBlock(deptId);
    try {
      await api.schuelerApp.wuenscheSave(deptId, state[deptId].map(p => +p.practiceId));
    } catch {
      showToast("Fehler beim Speichern – bitte erneut versuchen");
    } finally {
      savingDepts.delete(deptId);
      rerenderBlock(deptId);
    }
  }

  function renderDeptBlock(dept: WunschDept): string {
    const list = state[dept.id];
    const available = verfuegbar[dept.id] || [];
    const usedIds = new Set(list.map(p => p.practiceId));
    const remaining = available.filter(p => !usedIds.has(p.id));
    const saving = savingDepts.has(dept.id);

    const listItems = list.length === 0
      ? `<div style="color:#9ca3af;font-size:.85rem;padding:.25rem 0">Keine Wünsche eingetragen</div>`
      : list.map((p, i) => `
          <div class="wish-item" data-dept="${esc(dept.id)}" data-idx="${i}" style="display:flex;align-items:center;gap:.5rem;padding:.3rem 0;border-bottom:1px solid #f3f4f6;min-width:0">
            <span style="color:#9ca3af;font-size:.85rem;flex-shrink:0;min-width:1.2rem">${i + 1}.</span>
            <span style="flex:1;font-size:.9rem;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(p.practiceName)}</span>
            <div class="wish-actions">
              <button type="button" class="wish-up wish-btn" data-dept="${esc(dept.id)}" data-idx="${i}" title="Höher" ${i === 0 || saving ? "disabled" : ""}>${Icons.chevronUp}</button>
              <button type="button" class="wish-down wish-btn" data-dept="${esc(dept.id)}" data-idx="${i}" title="Niedriger" ${i === list.length - 1 || saving ? "disabled" : ""}>${Icons.chevronDown}</button>
              <button type="button" class="wish-remove wish-btn wish-btn-danger" data-dept="${esc(dept.id)}" data-idx="${i}" title="Entfernen" ${saving ? "disabled" : ""}>${Icons.xMark}</button>
            </div>
          </div>`).join("");

    const addSelect = dept.orphaned
      ? `<div style="font-size:.8rem;color:#9ca3af;margin-top:.5rem">Dieser Fachbereich ist nicht mehr verfügbar – vorhandene Wünsche können hier entfernt werden.</div>`
      : available.length === 0
      ? `<div style="font-size:.8rem;color:#9ca3af;margin-top:.5rem">Für diesen Fachbereich sind aktuell keine Praktikumsstellen verfügbar.</div>`
      : remaining.length > 0
      ? `<div style="margin-top:.5rem;overflow:hidden">
           <select class="wish-add-select" data-dept="${esc(dept.id)}" ${saving ? "disabled" : ""} style="width:100%;box-sizing:border-box;font-size:.85rem;padding:.3rem .5rem;border:1px solid #d1d5db;border-radius:.375rem">
             <option value="">+ Praktikumsstelle hinzufügen …</option>
             ${remaining.map(p => `<option value="${p.id}">${esc(p.name)}${p.ort ? " – " + esc(p.ort) : ""}</option>`).join("")}
           </select>
         </div>`
      : `<div style="font-size:.8rem;color:#9ca3af;margin-top:.5rem">Alle verfügbaren Stellen bereits in der Liste</div>`;

    return `
      <div class="wish-dept-block" data-dept="${esc(dept.id)}" style="margin-bottom:1rem">
        <div style="display:flex;align-items:center;gap:.5rem;margin-bottom:.5rem">
          <div style="width:10px;height:10px;border-radius:50%;background:${dept.color};flex-shrink:0"></div>
          <strong style="font-size:.9rem">${esc(dept.nameDe)}</strong>
        </div>
        <div class="wish-list" data-dept="${esc(dept.id)}">${listItems}</div>
        ${addSelect}
      </div>`;
  }

  function rerenderBlock(deptId: string) {
    const block = container.querySelector(`.wish-dept-block[data-dept="${deptId}"]`);
    if (block) block.outerHTML = renderDeptBlock(depts.find(d => d.id === deptId)!);
  }

  // Event Delegation – einmalig am Container, kein Re-Attach nach Re-Render nötig.
  // closest("button"): Klicks landen oft auf dem inneren SVG-Icon statt dem Button selbst
  // (kein data-dept dort) – ohne closest() würde der Klick sonst folgenlos verpuffen.
  container.addEventListener("click", async (e) => {
    const el = (e.target as HTMLElement).closest("button");
    if (!el) return;
    const dept = el.dataset.dept;
    if (!dept || savingDepts.has(dept)) return;

    if (el.classList.contains("wish-up")) {
      const idx = +el.dataset.idx!;
      if (idx === 0) return;
      const arr = state[dept];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      await saveWunschliste(dept);
    } else if (el.classList.contains("wish-down")) {
      const idx = +el.dataset.idx!;
      const arr = state[dept];
      if (idx >= arr.length - 1) return;
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      await saveWunschliste(dept);
    } else if (el.classList.contains("wish-remove")) {
      const idx = +el.dataset.idx!;
      state[dept].splice(idx, 1);
      await saveWunschliste(dept);
    }
  });

  container.addEventListener("change", async (e) => {
    const el = e.target as HTMLSelectElement;
    if (!el.classList.contains("wish-add-select")) return;
    const dept = el.dataset.dept!;
    if (savingDepts.has(dept)) return;
    const practiceId = +el.value;
    if (!practiceId) return;
    // +p.id: BIGINT kommt von pg als String – Typ-sicherer Vergleich
    const practice = verfuegbar[dept].find(p => +p.id === practiceId);
    if (!practice) return;
    state[dept].push({ practiceId: practice.id, practiceName: practice.name });
    await saveWunschliste(dept);
  });

  container.innerHTML = `
    <div class="card" style="padding:1.25rem;margin-top:1.5rem">
      <h3 style="font-size:.95rem;font-weight:600;margin:0 0 1rem">Meine Wunschlisten</h3>
      <p style="font-size:.85rem;color:#6b7280;margin:0 0 1rem">Trage hier ein, welche Praktikumsstellen du dir pro Fachbereich wünschst. Die Reihenfolge bestimmt die Priorität (1 = höchster Wunsch).</p>
      ${depts.map(d => renderDeptBlock(d)).join("")}
    </div>`;
}

/** Ohne Planungs-Modul bleibt von der Seite nur die Wunschliste übrig. */
async function renderWunschlisteOnly(el: HTMLElement) {
  el.innerHTML = `
    <div class="page-header"><h1>Meine Planung</h1></div>
    <div id="wunschliste-container"></div>`;
  const wc = el.querySelector("#wunschliste-container") as HTMLElement;
  if (wc) await renderWunschliste(wc);
}

export async function render(el: HTMLElement) {
  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;

  // Ohne Planungs-Modul ist "Meine Planung" gesperrt und der Endpunkt antwortet mit 403.
  // Den Umweg über den Fehlertext sparen wir uns.
  if (!hasPortalFeature("meinePlanung")) {
    await renderWunschlisteOnly(el);
    return;
  }

  try {
    const data: StudentPlanungData = await api.schuelerApp.planung();

    if (!data.period) {
      el.innerHTML = `
        <div class="page-header"><h1>Meine Planung</h1></div>
        <div class="card" style="padding:2rem;text-align:center;color:#9ca3af">
          <p>Kein aktiver Planungszeitraum vorhanden.</p>
        </div>
        <div id="wunschliste-container"></div>`;
      const wc = el.querySelector("#wunschliste-container") as HTMLElement;
      if (wc) await renderWunschliste(wc);
      return;
    }

    const { period, assignments, hours } = data;
    const futurePeriods = data.futurePeriods || [];
    const pastPeriods = data.pastPeriods || [];
    const hasAssignments = assignments.length > 0;
    const totalRequired = hours.reduce((s, h) => s + h.targetHours, 0);
    const totalCompleted = hours.reduce((s, h) => s + Math.min(h.completedHours, h.targetHours), 0);

    const allAssignmentsFlat = [
      ...pastPeriods.flatMap(pp => pp.assignments),
      ...assignments,
      ...futurePeriods.flatMap(fp => fp.assignments),
    ];
    const currentAssignment = assignments.find(a => a.isCurrent);
    const upcomingCount = assignments.filter(a => a.isUpcoming).length + futurePeriods.reduce((s, fp) => s + fp.assignments.length, 0);
    const doneCount = allAssignmentsFlat.filter(a => !a.isCurrent && !a.isUpcoming).length;

    // Tabelleninhalt: vergangene Perioden + primäre Periode + zukünftige Perioden mit Trennzeilen
    const hasPastAssignments = pastPeriods.some(pp => pp.assignments.length > 0);
    const hasFutureAssignments = futurePeriods.some(fp => fp.assignments.length > 0);
    const tbodyRows = (hasPastAssignments || hasAssignments || hasFutureAssignments)
      ? [
          ...pastPeriods.flatMap(pp => [
            renderPeriodSeparator(pp.period.name, pp.period.startDate, pp.period.endDate),
            ...pp.assignments.map(a => renderAssignmentRow(a)),
          ]),
          ...assignments.map(a => renderAssignmentRow(a)),
          ...futurePeriods.flatMap(fp => [
            renderPeriodSeparator(fp.period.name, fp.period.startDate, fp.period.endDate),
            ...fp.assignments.map(a => renderAssignmentRow(a)),
          ]),
        ].join("")
      : `<tr><td colspan="5" class="empty-state">Noch keine Einsätze geplant.</td></tr>`;

    el.innerHTML = `
      <div class="page-header">
        <h1>Meine Planung</h1>
        <div style="font-size:.875rem;color:#6b7280">${esc(period.name)}</div>
      </div>

      ${hours.length > 0 ? `
      <!-- Stunden-Übersicht -->
      <div class="card" style="padding:1.25rem;margin-bottom:1.5rem">
        <h3 style="font-size:.95rem;font-weight:600;margin:0 0 1rem">Stundenübersicht</h3>
        <div style="display:flex;flex-direction:column;gap:.6rem">
          ${hours.map(h => `
            <div class="student-hours-row">
              <span class="student-hours-label">${esc(h.nameDe)}</span>
              ${renderProgressBar(h.completedHours, h.targetHours, h.color)}
              <span class="student-hours-value">${h.completedHours} / ${h.targetHours} Std.</span>
            </div>`).join("")}
        </div>
        <div style="margin-top:1rem;padding-top:.75rem;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;font-size:.875rem">
          <span style="color:#6b7280">Gesamt</span>
          <strong>${totalCompleted} / ${totalRequired} Stunden (${totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 0}%)</strong>
        </div>
      </div>` : ""}

      <!-- Statistik-Kacheln -->
      <div class="student-stats-grid">
        <div class="card" style="padding:1rem;text-align:center">
          <div style="font-size:1.5rem;font-weight:700;color:#059669">${doneCount}</div>
          <div style="font-size:.8rem;color:#6b7280;margin-top:.25rem">Abgeschlossen</div>
        </div>
        <div class="card" style="padding:1rem;text-align:center;border:${currentAssignment ? "2px solid #059669" : ""}">
          <div style="font-size:1.5rem;font-weight:700;color:${currentAssignment ? "#059669" : "#9ca3af"}">${currentAssignment ? "1" : "–"}</div>
          <div style="font-size:.8rem;color:#6b7280;margin-top:.25rem">Aktuell</div>
        </div>
        <div class="card" style="padding:1rem;text-align:center">
          <div style="font-size:1.5rem;font-weight:700;color:#2563eb">${upcomingCount}</div>
          <div style="font-size:.8rem;color:#6b7280;margin-top:.25rem">Demnächst</div>
        </div>
      </div>

      <!-- Alle Einsätze -->
      <div class="card">
        <div style="padding:1rem 1.25rem .5rem;display:flex;align-items:center;justify-content:space-between">
          <span style="font-size:.95rem;font-weight:600">Alle Einsätze</span>
          ${(hasPastAssignments || hasAssignments || hasFutureAssignments) ? `<button id="btn-export-ics" class="btn btn-secondary" style="font-size:.8rem;padding:.3rem .75rem">Kalender exportieren (.ics)</button>` : ""}
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Fachbereich</th>
                <th>Praktikumsstelle</th>
                <th>Zeitraum</th>
                <th>Dauer / Stunden</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${tbodyRows}
            </tbody>
          </table>
        </div>
      </div>
      <div id="wunschliste-container"></div>`;

    // Assignment-Map für Klick-Handler aufbauen
    const assignmentMap = new Map<string, StudentAssignment>();
    for (const a of allAssignmentsFlat) assignmentMap.set(String(a.id), a);

    const tbody = el.querySelector("tbody");
    tbody?.addEventListener("click", (e) => {
      const row = (e.target as HTMLElement).closest("tr[data-assignment-id]") as HTMLElement | null;
      if (!row) return;
      const a = assignmentMap.get(row.dataset.assignmentId!);
      if (a) showAssignmentDetail(a);
    });

    const exportBtn = el.querySelector("#btn-export-ics") as HTMLButtonElement | null;
    if (exportBtn) {
      exportBtn.addEventListener("click", () => {
        const allAssignments = allAssignmentsFlat;
        const icsContent = generateIcs(allAssignments);
        const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "meine-planung.ics";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      });
    }

    const wunschContainer = el.querySelector("#wunschliste-container") as HTMLElement;
    if (wunschContainer) await renderWunschliste(wunschContainer);

  } catch (err: any) {
    // Netz, falls die Schule während der Sitzung umschaltet: Der Endpunkt sperrt
    // entweder über das Modul ("nicht freigeschaltet") oder über die Portal-Funktion
    // ("nicht aktiviert"). In beiden Fällen bleibt die Wunschliste übrig.
    if (err.message?.includes("nicht freigeschaltet") || err.message?.includes("nicht aktiviert")) {
      await renderWunschlisteOnly(el);
    } else {
      el.innerHTML = `<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
    }
  }
}
